import type { PropsWithChildren } from "react";

import Button from "~/components/buttons/Button/Button";
import type { CardVariantProps } from "~/components/Card/cardVariants.ts";
import CardContent from "~/components/Card/components/CardContent";
import CardHeader from "~/components/Card/components/CardHeader";
import CardTitle from "~/components/Card/components/CardTitle";
import CardWrapper from "~/components/Card/components/CardWrapper";
import Tooltip from "~/components/Tooltip";

type HeaderButtonActionProps =
	| { onHeaderButtonClick?: () => void; headerButtonTo?: never }
	| { onHeaderButtonClick?: never; headerButtonTo?: string };

type CardProps = PropsWithChildren & CardVariantProps & HeaderButtonActionProps & {
	title?: string;
	headerButtonText?: string;
	headerButtonDisabled?: boolean;
	// Shows in a tooltip on the disabled header button.
	headerButtonDisabledReason?: string | null;
};

const Card: React.FunctionComponent<CardProps> = ({
		title,
		headerButtonText,
		headerButtonDisabled = false,
		headerButtonDisabledReason,
		onHeaderButtonClick,
		headerButtonTo,
		children,
		...variantProps
	}) => {
		if (onHeaderButtonClick && headerButtonTo) {
			throw new Error("Card: provide either onHeaderButtonClick or headerButtonTo, not both.");
		}

		const hasHeaderButtonAction = !!onHeaderButtonClick || !!headerButtonTo;

		return <CardWrapper hasTitle={!!title} {...variantProps}>
			{title && <CardHeader hasTitle={!!title} {...variantProps}>
				<div>
					<CardTitle>{title}</CardTitle>
				</div>

				{headerButtonText && hasHeaderButtonAction && headerButtonDisabled && <Tooltip text={headerButtonDisabledReason ?? ""}>
					{/* A disabled button gets no mouse events, so the span carries the tooltip. */}
					<span>
						<Button size="auto"
								theme="plain"
								className="px-2 py-1 text-xs"
								disabled={true}>{headerButtonText}</Button>
					</span>
				</Tooltip>}
				{headerButtonText && hasHeaderButtonAction && !headerButtonDisabled && <Button size="auto"
																							   theme="plain"
																							   className="px-2 py-1 text-xs"
																							   onClick={onHeaderButtonClick}
																							   to={headerButtonTo}>{headerButtonText}</Button>}
			</CardHeader>}
			<CardContent hasTitle={!!title} {...variantProps}>{children}</CardContent>
		</CardWrapper>;
	}
;

export default Card;